<template lang="pug">
.b-project-files(v-if="categories && uploadedFiles")
  template(v-if="selectMode")
    FileListComp._files(v-if="allSelectedFiles.length" :files="allSelectedFiles" @remove="onToggleFile")
    Btn._btn-bind(@click="openPopup" dark)
      Icon._icon(name="upload")
      | Прикрепить файлы
  ._btn-bind-container(v-else)
    Btn._btn-bind(@click="openPopup" dark)
      Icon._icon(:name="buttonIcon || 'upload'")
      | {{ buttonText || 'Прикрепить файлы' }}
    div(v-if="!noUploadedText && uploadedFiles.length") Файлов: {{ uploadedFiles.length }}
    div(v-else-if="!noUploadedText") Нет файлов
  ProjectFileCategoryListPopup(
    ref="categoryListPopup"
    :items="categories"
    :selected="selectedCategory"
    :filesCount="fileCountInCategories"
    @select="selectedCategory = $event"
  )
  ProjectFileUploadPopup(
    ref="uploadPopup"
    :allFiles="allFiles"
    :uploadingFilesCount="uploadingFilesCount"
    :selectedCategory="selectedCategory"
    @upload="onFilesSelected"
    @delete="onDeleteFile"
    @toggle="onToggleFile"
    @select="onSelectFilesInCategory"
  )
    template(#categories)
      ProjectFileCategoryList(
        :items="categories"
        :selected="selectedCategory"
        :filesCount="fileCountInCategories"
        @select="selectedCategory = $event"
      )

</template>

<script>
import fileApi from '@/api/file';
import projectFileApi from '@/api/projectFile';

import { v4 as uuidv4 } from 'uuid';

import FileListComp from '@/components/FileList.vue';
import ProjectFileCategoryList from '@/components/ProjectFileCategoryList.vue';
import ProjectFileCategoryListPopup from '@/components/ProjectFileCategoryListPopup.vue';
import ProjectFileUploadPopup from '@/components/ProjectFileUploadPopup.vue';
import BasePopup from '@/components/BasePopup.vue';

export default {
  name: 'ProjectFileUploader',
  components: { BasePopup, ProjectFileCategoryList, FileListComp, ProjectFileUploadPopup, ProjectFileCategoryListPopup }, // eslint-disable-line
  props: {
    project: Number,
    selected: Array,
    buttonText: String,
    buttonIcon: String,
    noUploadedText: Boolean,
    publish: Boolean,
    filesToUpload: [Array, FileList],
  },
  data() {
    return {
      selectedCategory: null,
      uploadedFiles: [],
      uploadingFiles: [],
      deletingFileIris: [],
      isCategoryPopupOpened: false,
    };
  },
  watch: {
    filesFromState: {
      immediate: true,
      handler(val) { this.uploadedFiles = val ?? []; },
    },
    uploadingFilesCount(val) {
      this.$store.commit('closePrevent/set', val);
    },
    filesToUpload(val) {
      if (val.length) {
        this.uploadExternalFiles(val);
      }
    },
  },
  computed: {
    projectId() { return this.project ?? this.$route.params.projectId; },
    selectMode() { return !!this.selected; },
    filesFromState() { return this.$store.state.projectFile.files[this.projectId]; },
    categories() { return this.$store.state.projectFile.categories[this.projectId]; },
    uploadingFilesCount() { return this.uploadingFiles.filter(f => !f.error).length; },
    allSelectedFiles() {
      return [...this.uploadingFiles, ...this.selectedFiles];
    },
    allFiles() {
      return [
        ...this.uploadingFiles,
        ...this.uploadedFiles.map((item) => ({
          ...item,
          removable: item.isDraft,
          selectable: this.selectMode,
          selected: this.selected && this.selected.includes(item['@id']),
        })),
      ];
    },
    selectedFiles() {
      if (!this.selectMode || !this.uploadedFiles) {
        return [];
      }

      return this.uploadedFiles
        .filter((item) => this.selected.includes(item['@id']))
        .map((item) => ({
          ...item,
          removable: true,
        }));
    },
    fileCountInCategories() {
      const categories = this.$store.state.projectFile.categories[this.projectId] ?? [];
      const counts = Object.fromEntries(categories.map((category) => [category['@id'], 0]));

      this.allFiles.forEach((file) => { counts[file.category] += 1; });

      return counts;
    },
  },
  created() {
    this.$store.dispatch('projectFile/fetchCategories', { projectId: this.projectId, force: true });
    this.$store.dispatch('projectFile/fetchFiles', { projectId: this.projectId, force: true });
  },
  methods: {
    openPopup() {
      this.$store.dispatch('projectFile/fetchFiles', { projectId: this.projectId, force: true });

      if (!this.selectedCategory) {
        this.selectedCategory = this.categories?.[0];
      }

      this.$refs.uploadPopup.open();
    },
    onToggleFile(file) {
      const iri = file['@id'];
      const updatedValue = this.selected.includes(iri)
        ? this.selected.filter((item) => item !== iri)
        : [...this.selected, iri];

      this.$emit('select', updatedValue);
    },
    onSelectFilesInCategory(files) {
      const selected = [...new Set([...this.selected, ...files])];
      this.$emit('select', selected);
    },
    async uploadExternalFiles(files) {
      if (!this.selectedCategory) {
        this.selectedCategory = this.categories?.[0];
      }

      const res = await this.$refs.categoryListPopup.open();

      if (!res) return;

      this.onFilesSelected(files);
    },
    onDeleteFile(file) {
      const iri = file['@id'];
      const uploadingFile = this.uploadingFiles.find((item) => item['@id'] === iri);

      if (uploadingFile) {
        uploadingFile.controller.abort();
        this.uploadingFiles = this.uploadingFiles.filter((item) => item !== uploadingFile);
        return;
      }

      const uploadedFile = this.uploadedFiles.find((item) => item['@id'] === iri);
      uploadedFile.process = 'deleting';

      projectFileApi.delete(uploadedFile.id, true)
        .then(() => {
          this.$store.commit('projectFile/setFiles', {
            projectId: this.projectId,
            files: this.uploadedFiles.filter((item) => item['@id'] !== iri),
          });
        })
        .catch(() => {
          uploadedFile.process = 'deleting';
          uploadedFile.error = 'Ошибка удаления';
        });
    },
    onFilesSelected(files) {
      const category = this.selectedCategory;
      files.forEach((file) => {
        const uploadingFile = {
          file,
          error: '',
          '@id': uuidv4(),
          category: category['@id'],
          progress: 0,
          process: 'waiting',
          removable: true,
          controller: new AbortController(),
        };

        this.uploadingFiles = [uploadingFile, ...this.uploadingFiles];
        this.uploadFile(uploadingFile, category);
      });
    },
    async uploadFile(uploadingFile, category) {
      if (uploadingFile.file.size > 2147483647) {
        uploadingFile.error = 'Максимальный размер файла 2ГБ'; // eslint-disable-line no-param-reassign
        uploadingFile.process = null; // eslint-disable-line no-param-reassign
        return;
      }

      const progressCallback = (progress) => {
        uploadingFile.progress = progress;                                // eslint-disable-line
        uploadingFile.process = progress > 0 ? 'uploading' : 'waiting';   // eslint-disable-line
      };

      try {
        const createdFile = await fileApi.create(uploadingFile.file, progressCallback, uploadingFile.controller);
        uploadingFile.removable = false; // eslint-disable-line no-param-reassign
        const createdProjectFile = await projectFileApi.create({
          isDraft: !this.publish,
          category: category['@id'],
          file: createdFile.data['@id'],
        });

        this.uploadingFiles = this.uploadingFiles.filter((item) => item !== uploadingFile);
        this.$store.commit('projectFile/setFiles', {
          projectId: this.projectId,
          files: [createdProjectFile, ...this.uploadedFiles],
        });

        this.$emit('select', [...this.selected ?? [], createdProjectFile['@id']]);
      } catch (e) {
        uploadingFile.process = null; // eslint-disable-line
        uploadingFile.error = 'Ошибка загрузки'; // eslint-disable-line
      }
    },
  },
};
</script>

<style scoped lang="scss">
.project-files {
  display: flex;
  flex-direction: column;
  gap: 30px;

  &__btn-bind-container {
    display: flex;
    align-items: center;
    gap: 30px;
  }

  &__btn-bind {
    align-self: start;
  }

  &__btns {
    display: flex;
    gap: 30px;
    justify-content: flex-end;

    &--right {
      justify-content: flex-start;
    }
  }
}
</style>
